/* The demo's sign-in gate.
 *
 * READ THIS BEFORE TRUSTING IT: THIS IS NOT SECURITY.
 *
 * The credentials are compared in the browser, in code the browser downloaded.
 * Anyone can read them from the JS bundle, and anyone can set the session key
 * by hand in devtools. It stops nobody who does not want to be stopped.
 *
 * That is deliberate and it is the right scope. Requirement 8.5.1 puts
 * "multi-user auth" explicitly out of scope for this PoC, and the API behind
 * this screen has no authentication either — a real login here would be a
 * costume, implying a protection that does not exist one layer down. What this
 * IS: a front door for the demo, so the client sees the product open the way a
 * product opens rather than dropping straight into a work queue.
 *
 * WHEN IT BECOMES REAL. The whole surface is `signIn`/`signOut`/`isSignedIn`.
 * Real auth replaces the body of those three with a call to the API, and adds
 * the server side that actually matters. Nothing else in the app knows how
 * sign-in works, which is the point of putting it behind three functions.
 */

const KEY = "ego.session";

/** The demo accounts. Not secrets — they are in the bundle either way, and
 *  pretending otherwise by obfuscating them would only make them harder to
 *  find when they need changing.
 *
 *  TWO ROLES, ONE PERMISSION SET. QA can do everything the annotator can; the
 *  difference is WHICH VIDEOS EACH SEES. The annotator's queue is work to do,
 *  so it lists videos still awaiting review; QA's is work to check, so it lists
 *  only what the annotator has finished with. Same screens, different slice —
 *  which is why this is a filter in the queue and not a permission anywhere. */
const ACCOUNTS: Record<string, { password: string; role: Role; name: string }> = {
  // 23 Oct 2026 (Utsav): both passwords changed (from "crowd" and "qa").
  // 27 Oct 2026 (manager's review): the annotator login is user0108, and the
  // header shows user0108 rather than a made-up person's name. "crowd" no
  // longer signs in. Password changed again the same day (manager's follow-up).
  user0108: { password: "anno!@#45", role: "annotator", name: "user0108" },
  qa:    { password: "shaip@2026", role: "qa",        name: "Ravi K." },
};

export type Role = "annotator" | "qa";

export interface Session {
  username: string;
  /** Shown in the header. The prototype names the person, not the login. */
  name: string;
  role: Role;
  since: string;
}

/** sessionStorage, not localStorage: the session ends when the tab closes.
 *  On a shared demo laptop that is the behaviour people expect, and it means a
 *  stale session cannot greet the next person who opens the browser. */
export function currentSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    // Private-mode browsers throw on sessionStorage rather than returning null.
    // Treat that as "not signed in" instead of taking the whole app down.
    return null;
  }
}

export const isSignedIn = () => currentSession() !== null;

/** Returns null on success, or the message to show. */
export function signIn(username: string, password: string): string | null {
  const u = username.trim();
  if (!u || !password) return "Enter your username and password.";
  // Deliberately does NOT say which of the two was wrong. That is the one
  // habit worth keeping from real auth: it costs nothing and it is the correct
  // reflex to build.
  const account = ACCOUNTS[u.toLowerCase()];
  if (!account || password !== account.password)
    return "That username and password do not match.";

  const session: Session = {
    username: u.toLowerCase(), name: account.name, role: account.role,
    since: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    return "This browser is blocking session storage, so sign-in cannot persist.";
  }
  return null;
}

export function signOut(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The signed-in person's role, defaulting to annotator.
 *
 *  Sessions written before QA existed have no `role` field. Treating that as
 *  "annotator" keeps an open tab working across the deploy instead of throwing
 *  a blank queue at whoever was mid-review. */
export const currentRole = (): Role => currentSession()?.role ?? "annotator";

/** The name to show in the header — never the login. Falls back to the
 *  username for a pre-role session. */
export const currentName = (): string =>
  currentSession()?.name ?? currentSession()?.username ?? "";
